import React, { useMemo, useState, useEffect, useContext } from "react";
import { useNavigate } from 'react-router-dom';
import { Box, Grid, } from "@mui/material";
import { useTranslation } from "react-i18next";
import { GET_IMPORT_SHIPMENT_DUE_DATE_REPORT, } from "../../services/apiRoutes";
import { AuthContext } from "../../context/AuthContext";
import { getColorById, getColorPlate, getSuppliersList } from "../../services/CommonServices";
import CustomLoader from "../common/CustomLoader";
import CustomReport from "../common/CustomReport";
import { genericPost } from "../../services/authServices";
import CustomTextField from "../common/CustomTextField";
import CustomFilter from "../common/CustomFilter";
import CustomButton from "../common/CustomButton";
import FindReplaceOutlinedIcon from '@mui/icons-material/FindReplaceOutlined';
import { ClearRounded, } from "@mui/icons-material";
import CustomAutocomplete from "../common/CustomAutoComplete";
import { useCompanyDateFormat } from "../../constants/useCompanyDateFormat";
import CustomDataTable from "../common/CustomDataTable";
import { AccessDenied, hasCreatePermission, hasDeletePermission, hasEditPermission, hasViewPermission } from "../../services/CommonServices";
import { useLocation } from "react-router-dom";
import CustomStatus from "../common/CustomStatus";


const title = "Due Date Import Shipment Report";

const DueDateImportShipmentReport = () => {
    const companyDateFormat = useCompanyDateFormat();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const { token } = useContext(AuthContext)
    const [loading, setLoading] = useState(false);
    const [permission, setPermissions] = useState()
    const [error, setError] = useState(null);
    const filterOpen = () => setOpenDrawer(true);
    const filterClose = () => setOpenDrawer(false);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [suppliersList, setSuppliersList] = useState([]);
    const [page, setPage] = useState(0); // UI 0-based
    const [pageSize, setPageSize] = useState(10);
    const [colorlist, setColorlist] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [filterPayload, setFilterPayload] = useState({
        shipmentId: '',
        supplierNamesId: '',
        fromDate: '',
        toDate: '',
    });
    const location = useLocation();
    const currentPath = location.pathname;
    useEffect(() => {
        let rolePermisionsData = JSON.parse(sessionStorage.getItem('userData')) || []
        let editPermision = hasEditPermission(currentPath, rolePermisionsData?.permissions)
        let deletePermission = hasDeletePermission(currentPath, rolePermisionsData?.permissions)
        let createPermision = hasCreatePermission(currentPath, rolePermisionsData?.permissions)
        let viewPermision = hasViewPermission(currentPath, rolePermisionsData?.permissions)

        setPermissions({
            edit: editPermision,
            delete: deletePermission,
            create: createPermision,
            view: viewPermision,
        })
    }, [])
    const handleFilterChange = (field, value) => {
        setFilterPayload((prev) => ({
            ...prev,
            [field]: value || '',
        }));
    };
    const handleClearFilters = useMemo(() => {
        return () => {
            setFilterPayload({
                shipmentId: '',
                supplierNamesId: '',
                fromDate: '',
                toDate: '',
            });
            fetchData(0, pageSize, '');
        };
    }, [pageSize]);
    useEffect(() => {
        getCommonData();
    }, []);

    const getCommonData = async () => {
        const suppliersListData = await getSuppliersList();
        setSuppliersList(suppliersListData);
        const colorData = await getColorPlate();
        setColorlist(colorData);
    }


    const columns = useMemo(
        () => [
            { accessorKey: "index", header: "SN.", size: 50, tableHeader: { alignItems: 'center', }, tableStyle: { textAlign: "center" }, enableColumnFilter: false },
            { accessorKey: 'date', header: 'Date', size: 50, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'commercialInvoiceNo', header: 'CI No', size: 50, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'shipmentId', header: 'Shipment ID', size: 50, tableStyle: { textAlign: "center" }, },
            {
                accessorKey: 'clientCompanyName', header: 'Supplier Name', size: 50, tableHeader: {
                    justifyContent: 'flex-start',
                },
            },

            {
                accessorKey: "billingCountry",
                header: "Country",
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <CustomStatus
                            title={row.original.billingCountry}
                            sx={{
                                backgroundColor: row.original.countryIdBackground,
                                color: row.original.countryIdColor,
                                border: `1px solid ${row.original.countryIdColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },
            { accessorKey: 'amount', header: 'Amount', size: 50, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'advance', header: 'advance', size: 50, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'final', header: 'final', size: 50, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'totalBalance', header: 'Balance', size: 50, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'creditDay', header: 'Credit Day', size: 50, tableStyle: { textAlign: "center" }, },
            { accessorKey: 'dueDate', header: 'due Date', size: 50, tableStyle: { textAlign: "center" }, },
            // { accessorKey: 'remainingDay', header: 'Day Status', size: 50, tableStyle: { textAlign: "center" }, },
            {
                accessorKey: 'remainingDay', header: 'Day Status',
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <CustomStatus
                            title={row.original.remainingDay}
                            sx={{
                                backgroundColor: row.original.statusBackground,
                                color: row.original.statusColor,
                                border: `1px solid ${row.original.statusColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },
        ],
        [permission]
    );

    const fetchData = async (pageIndex = 0, pageSize = 10) => {
        setLoading(true);
        try {
            const payload = {
                page: pageIndex + 1,
                limit: pageSize,
                ...filterPayload,
            };

            const response = await genericPost(GET_IMPORT_SHIPMENT_DUE_DATE_REPORT, payload, {});

            if (response.data.success && response.data.data?.length) {

                const shipmentData = response.data.data.map((item, index) => {
                    const countryColorObj = getColorById(
                        item.clientCompany?.billingCountry?.id,
                        colorlist,
                        {
                            matchKey: "colorId",
                            prefixLength: 1,
                        });
                    // @@@@@@@@@@@@@@@@@
                    const balance = Number(item.totalBalanceAmount || 0);
                    const rawDueDate = item.onBoardDate ? item.onBoardDate : item.dueDate;
                    let remainingDayText = "";
                    let statusColor = "";
                    let statusBackground = "";
                    if (balance <= 0) {
                        // ✅ Payment Done
                        remainingDayText = "Payment Done";
                        statusColor = "#1B5E20";          // dark green
                        statusBackground = "#C8E6C9";    // light green
                    } else if (rawDueDate) {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const due = new Date(rawDueDate);
                        due.setHours(0, 0, 0, 0);
                        const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
                        if (diffDays < 0) {
                            // ❌ Overdue
                            const overdueDays = Math.abs(diffDays);
                            remainingDayText = `${overdueDays} Over Days`;
                            statusColor = "#B71C1C";        // dark red
                            statusBackground = "#FFCDD2";  // light red
                        } else {
                            // ⏳ Remaining
                            remainingDayText = `${diffDays} Remaining Days`;
                            // Optional: different colors based on urgency
                            if (diffDays <= 3) {
                                statusColor = "#E65100";        // dark orange
                                statusBackground = "#FFE0B2";  // light orange
                            } else {
                                statusColor = "#0D47A1";        // dark blue
                                statusBackground = "#BBDEFB";  // light blue
                            }
                        }
                    }

                    return {
                        index: (index + 1).toString().padStart(2, "0"),
                        countryIdColor: countryColorObj.color || '',
                        countryIdBackground: countryColorObj.backgroundColor || '',
                        // mapping same keys from backend
                        commercialInvoiceId: item.commercialInvoiceId,
                        commercialInvoiceNo: item.commercialInvoiceNo,
                        shipmentId: item.shipmentId,

                        // formatting
                        date: companyDateFormat(item.date),
                        dueDate: companyDateFormat(item.onBoardDate ? item.onBoardDate : item.dueDate),

                        // client company name
                        clientCompanyName: item.clientCompany?.clientCompanyName || "",
                        billingCountry: item.clientCompany?.billingCountry?.name || "",

                        // currency formatting
                        amount: `${item.currency?.currency_symbol || ""} ${Number(item.totalCIAmount || 0).toFixed(2)}`,
                        advance: `${item.currency?.currency_symbol || ""} ${Number(item.totalAdvanceAmount || 0).toFixed(2)}`,
                        final: `${item.currency?.currency_symbol || ""} ${Number(item.totalFinalAmount || 0).toFixed(2)}`,
                        totalBalance: `${item.currency?.currency_symbol || ""} ${Number(item.totalBalanceAmount || 0).toFixed(2)}`,

                        creditDay: `${item.totalcreditDay || 0} Day`,
                        remainingDay: (() => {
                            const balance = Number(item.totalBalanceAmount || 0);
                            if (balance <= 0) {
                                return "Payment Done";
                            }
                            const rawDueDate = item.onBoardDate ? item.onBoardDate : item.dueDate;
                            if (!rawDueDate) return "";
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const due = new Date(rawDueDate);
                            due.setHours(0, 0, 0, 0);
                            const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
                            if (diffDays < 0) {
                                const overdueDays = Math.abs(diffDays);
                                return `${overdueDays} Over Days`;
                            } else {
                                return `${diffDays} Remaining Days`;
                            }
                        })(),

                        statusColor: statusColor,
                        statusBackground: statusBackground
                    };
                });
                setData(shipmentData);
                setRowCount(response.data.pagination?.totalRecords || 0);

            } else {
                setData([]);
                setError("No records found");
            }

        } catch (error) {
            console.error("Error fetching import shipments", error);
            setError("Error fetching import shipments");
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchData(page, pageSize)
    }, [token, page, pageSize, colorlist])

    const defaultView = 'duedateimportshipmentreport';
    const mainContent = (
        <>
            <CustomLoader open={loading} />
            <CustomDataTable
                title={title}
                columns={columns}
                data={data}
                handleFilterButton={filterOpen}
                fiterButtonTitile="Fiter"
                rowCount={rowCount}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                fromDate={filterPayload.fromDate || ""}
                toDate={filterPayload.toDate || ""}
            />
            <CustomFilter
                drawerTitle="Filter Options"
                drawerWidth={300}
                openDrawer={openDrawer}
                onClose={filterClose}
                drawerContent={
                    <>
                        <Box>
                            <Grid container spacing={1} sx={{ padding: 0, }}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        type="text"
                                        placeholder="Enter Shipment ID"
                                        value={filterPayload.shipmentId}
                                        onChange={(e) => handleFilterChange('shipmentId', e.target.value)}

                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomAutocomplete
                                        options={suppliersList}
                                        getOptionLabel={(option) => option.title}
                                        placeholder="Select Supplier"
                                        onChange={(event, newValue) => handleFilterChange("supplierNamesId", newValue?.id)}
                                        value={suppliersList.find((item) => item.id === filterPayload.supplierNamesId) || null}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="from Date"
                                        type="date"
                                        value={filterPayload.fromDate}
                                        onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                        placeholder="From Date"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        fieldLabel="to Date"
                                        type="date"
                                        value={filterPayload.toDate}
                                        onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                        placeholder="To Date"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12} sx={{ display: 'flex', alignItems: 'center', marginTop: "10px" }}>
                                    <CustomButton
                                        startIcon={<FindReplaceOutlinedIcon />}
                                        title="Apply"
                                        size="small"
                                        variant="contained"
                                        onClick={fetchData}
                                    />
                                    <CustomButton
                                        variant="outlined"
                                        title="Clear"
                                        startIcon={<ClearRounded />}
                                        onClick={handleClearFilters}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </>
                }
            />
        </>
    );
    const getMainContent = (mainContent, permission) => {
        return permission?.view ? mainContent : null;
    };
    return (
        <>
            <>
                <CustomReport
                    defaultView={defaultView}
                    mainContent={getMainContent(mainContent, permission)} />
                {!permission?.view && <AccessDenied />}
            </>
        </>
    );
};
export default DueDateImportShipmentReport;
